import InputError from "./InputError";
/**
 * Special validation just for newsletters. Same signature as the default export below.
 * @param {*} form
 * @returns {Object.<string, Error>}
 */
const validateNewsletter = form => {
  const errors = {};

  if (form.subject_line === "") {
    errors.subjectLine = new Error("Subject line is required");
  }

  if (!form.chunks.length) {
    errors.chunkLength = new Error(
      "Your newsletter must contain at least one item"
    );
  }

  for (const chunk of form.chunks) {
    if (chunk.headline === "") {
      errors.chunkHeadlines = new Error(
        "A headline is required for all newsletter items"
      );
    }

    if (!chunk.blocks.getCurrentContent().hasText() && !chunk.primary_image) {
      errors.chunkContents = new Error(
        "Each newsletter item must have body content or a featured image"
      );
    }

    if (errors.chunkHeadlines || errors.chunkContents) {
      break;
    }
  }

  if (!form.subscription || !form.subscription.sailthru_list_id) {
    errors.subscription = new Error(
      "A newsletter distribution name is required"
    );
  }

  return errors;
};

/**
 * Special validation just for stories. Same signature as the default export below.
 * @param {*} form
 * @returns {Object.<string, Error>}
 */
const validateStory = (form, publishAsBreaking) => {
  const errors = {};

  if (!form.content.getCurrentContent().hasText() && !form.primary_image) {
    errors.body = new InputError(
      "Body content or a featured image is required"
    );
    errors.content = true;
  }

  // Skip additional validation if this is being published as breaking news.
  if (publishAsBreaking) {
    return errors;
  }

  if (!form.primary_tag) {
    errors.primaryTag = new InputError("A primary tag is required");

    // NOTE: Makeshift solution to give sidebar/topbar links for story context
    // on which section has an error.
    errors.metadata = true;
  }

  if (!(form.topics && form.topics.length > 0)) {
    errors.topics = new InputError("At least one section is required");
    errors.metadata = true;
  }

  if (!form.og_description) {
    errors.socialDescription = new InputError(
      "A social description is required"
    );
    errors["social & seo"] = true;
  }

  return errors;
};

// NOTE: This is the current way of adding form validation to our inputs that we
// _may_ want to change in the future. It requires boilerplate throughout our
// code but is at least straightforward to understand and modify.
//
// In the validate utility functions here:
// - Add a unique key to the errors object to hook onto
// - Assign an Error (message only) or InputError (title and message for
// sidekicks) to the key for error conditions
// - For every assigned error, also set to true its corresponding section from
// the sidebar/topbar links, i.e. `errors.content = true`
//
// In the corresponding inputs on the form:
// - Pass `form.errors.key` in as the error prop - Pass `() =>
// validateFormAsync(form).catch(error => void error)` in as the onBlur prop to
// that visual cues for form validation are updated ASAP. We can catch the error
// and do nothing with it (the error is usually propagated as a banner message).

/**
 * Validates a "form" containing a Draft object, a headline and metadata.
 * @param {Object} form
 * @param {bool} isNewsletter - is it a newsletter
 * @returns {Object.<string, Error>|undefined}
 */
export default (form, isNewsletter = false, publishAsBreaking = false) => {
  let errors = {};

  if (!form.headline && !isNewsletter) {
    errors.headline = new InputError("A headline is required");
    errors.content = true;
  }

  // Don't require authors if publishing as breaking news
  if ((!form.authors || form.authors.length === 0) && !publishAsBreaking) {
    errors.authors = new InputError("At least one author is required");
    errors.metadata = true;
  }

  if (isNewsletter) {
    errors = { ...errors, ...validateNewsletter(form) };
  } else {
    errors = { ...errors, ...validateStory(form, publishAsBreaking) };
  }

  return Object.keys(errors).length > 0 ? errors : undefined;
};
